import { useId, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AttestationSignerRole,
  SIGNER_ROLE_LABELS,
  type CommitteeAttestation,
  type CommitteeAttestationInput,
  type CommitteeFactsDto,
} from '@signalvote/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useCommitteeFacts, useLastCommitteeSaveDoor, useSaveCommitteeFacts, type CommitteeSaveDoor } from '@/hooks/queries';
import { queryKeys } from '@/lib/queryKeys';
import { committeeFactError } from './committeeFactErrors';
import { FIELD_CLASS } from './fieldClass';

type SignerKey = 'signerName' | 'signerRole' | 'signerRoleOther' | 'signerEmail';

/** Whether the saved facts carry a signature that still stands. */
export function isSigned(committee: CommitteeFactsDto): boolean {
  return !!committee.attestation?.attestedAt;
}

function signerRoleWords({ signerRole, signerRoleOther }: Pick<CommitteeAttestation, 'signerRole' | 'signerRoleOther'>): string {
  if (signerRole === AttestationSignerRole.Other) return signerRoleOther ?? '';
  return signerRole ? SIGNER_ROLE_LABELS[signerRole].toLowerCase() : '';
}

/** "Signed as Jane Smith, treasurer." while the signature stands; null before it does. */
export function signedAsWords(committee: CommitteeFactsDto): string | null {
  const signer = committee.attestation;
  if (!signer || !isSigned(committee)) return null;
  return `Signed as ${signer.signerName}, ${signerRoleWords(signer)}.`;
}

/** Why the signature is off, by the form that last saved: the web can't tell after a reload. */
const WITHDRAWN: Record<CommitteeSaveDoor | 'unknown', string> = {
  signature: 'You took your signature off. Tick the box to sign again.',
  facts: 'Your committee details changed after you signed, so check them and sign again.',
  unknown: 'Your signature is off. Check your committee details, then sign again.',
};

/**
 * The signer confirms the committee facts. Until signed the box reads a plain confirmation: the
 * server's statement carries blanks for a signer it hasn't saved yet. Once signed it reads the
 * server's statement, the words that count as signed. Ticking the box is the
 * signature: an attested save of the facts as they stand. Unticking saves them unattested, which
 * withdraws it. `beforeSign` lets the committee step finish saving its answers first.
 */
export function ExpressAttestation({ beforeSign }: { beforeSign?: () => Promise<boolean> }) {
  const id = useId();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const committee = useCommitteeFacts().data;
  const signer = committee?.attestation;
  const [values, setValues] = useState<Record<SignerKey, string>>(() => ({
    signerName: signer?.signerName ?? (user ? `${user.firstName} ${user.lastName}`.trim() : ''),
    signerRole: signer?.signerRole ?? '',
    signerRoleOther: signer?.signerRoleOther ?? '',
    signerEmail: signer?.signerEmail ?? user?.email ?? '',
  }));
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const { mutateAsync: saveFacts, isPending: saving } = useSaveCommitteeFacts('signature');
  const lastSave = useLastCommitteeSaveDoor();
  if (!committee) return null;
  const signed = isSigned(committee);

  const set = (key: SignerKey, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const sign = async (confirmed: boolean) => {
    setFormError(null);
    if (beforeSign && !(await beforeSign())) return;
    const latest = queryClient.getQueryData<CommitteeFactsDto>(queryKeys.messagingProfile.committee()) ?? committee;
    const role = (values.signerRole || null) as AttestationSignerRole | null;
    const attestation: CommitteeAttestationInput = {
      signerName: values.signerName.trim() || null,
      signerRole: role,
      signerRoleOther: role === AttestationSignerRole.Other ? values.signerRoleOther.trim() || null : null,
      signerEmail: values.signerEmail.trim() || null,
      confirmed,
    };
    try {
      await saveFacts({ facts: latest.facts, attestation });
    } catch (error) {
      const refusal = committeeFactError(error);
      if (refusal.field && refusal.field in values) setErrors({ [refusal.field]: refusal.message });
      else if (refusal.field) setFormError(`${refusal.message} Go back to About your committee to add it.`);
      else setFormError(refusal.message);
    }
  };

  const fieldProps = (key: SignerKey) => ({
    id: `${id}-${key}`,
    value: values[key],
    disabled: signed || saving,
    'aria-invalid': errors[key] ? true : undefined,
    'aria-describedby': errors[key] ? `${id}-${key}-error` : undefined,
  });
  const errorFor = (key: SignerKey) =>
    errors[key] && (
      <p id={`${id}-${key}-error`} className="text-sm text-destructive">
        {errors[key]}
      </p>
    );

  return (
    <fieldset className="space-y-4 rounded-xl border border-border p-5">
      <legend className="px-1 text-sm font-semibold text-foreground">Sign for your committee</legend>
      <p className="text-sm text-muted-foreground">Carriers need someone from the committee to vouch for these details.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-signerName`}>Your name</Label>
          <Input {...fieldProps('signerName')} autoComplete="name" onChange={(e) => set('signerName', e.target.value)} />
          {errorFor('signerName')}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-signerRole`}>Your role</Label>
          <select {...fieldProps('signerRole')} className={FIELD_CLASS} onChange={(e) => set('signerRole', e.target.value)}>
            <option value="">Choose…</option>
            {Object.entries(SIGNER_ROLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          {errorFor('signerRole')}
        </div>
        {values.signerRole === AttestationSignerRole.Other && (
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-signerRoleOther`}>Which role</Label>
            <Input {...fieldProps('signerRoleOther')} onChange={(e) => set('signerRoleOther', e.target.value)} />
            {errorFor('signerRoleOther')}
          </div>
        )}
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor={`${id}-signerEmail`}>Your email</Label>
          <Input {...fieldProps('signerEmail')} type="email" autoComplete="email" onChange={(e) => set('signerEmail', e.target.value)} />
          {errorFor('signerEmail')}
        </div>
      </div>
      {signer && !signed && <p className="text-sm font-medium text-foreground">{WITHDRAWN[lastSave ?? 'unknown']}</p>}
      <label className="flex items-start gap-3 text-sm text-foreground">
        <input
          type="checkbox"
          checked={signed}
          disabled={saving}
          aria-describedby={`${id}-statement`}
          onChange={(e) => void sign(e.target.checked)}
          className="mt-1 h-4 w-4 shrink-0 accent-primary"
        />
        <span id={`${id}-statement`}>
          {signed
            ? committee.attestationStatement
            : `I confirm the committee details above are accurate, and I authorize registering ${
                committee.facts.legalName ?? 'my committee'
              } for political text messaging.`}
        </span>
      </label>
      <p role="status" className="text-sm text-muted-foreground">
        {signed && `${signedAsWords(committee)} Untick to change.`}
      </p>
      {formError && (
        <p role="alert" className="text-sm text-destructive">
          {formError}
        </p>
      )}
    </fieldset>
  );
}
